/**
 * Lets people TYPE a year into a native date field.
 *
 * While a year is typed digit by digit, the browser reports the half-typed value:
 * typing 2026 gives "0002-…", "0020-…", "0202-…", then "2026-…"; clearing one part
 * of the field gives "" with `validity.badInput` set. Page handlers that turn the
 * value into a Date (`new Date(y, m, d)` reads years below 100 as 19xx), check it
 * against a range, or put the last good date back when it is empty, rewrite the
 * field mid-typing, and the year gets stuck at 19xx.
 *
 * This swallows `input` and `change` events from a date field while its value is
 * unfinished, before any page handler sees them (capture phase on the document), so
 * pages only ever hear about a complete date. A field cleared on purpose (every part
 * empty, so no badInput) still gets through. Picking from the calendar is unaffected.
 *
 * Installed once by app.component; `isActive` limits it to the accounting pages.
 */
export function installDateTypingGuard(doc: Document, isActive: () => boolean): void {
    const swallowUnfinished = (event: Event) => {
        const el = event.target;
        if (!(el instanceof HTMLInputElement) || el.type !== 'date') return;
        if (!isActive()) return;

        const value = el.value;
        const halfFilled = value === '' && !!el.validity?.badInput;
        const year = /^(\d{4,})-/.exec(value);
        const yearStillTyping = !!year && Number(year[1]) < 1000;

        if (halfFilled || yearStillTyping) {
            event.stopImmediatePropagation();
        }
    };
    doc.addEventListener('input', swallowUnfinished, true);
    doc.addEventListener('change', swallowUnfinished, true);
}

/** A date field's value while its year is still being typed ("0002-…" … "0202-…"). */
export function isUnfinishedDateValue(value: any): boolean {
    return typeof value === 'string' && /^0\d{3}-/.test(value);
}

/**
 * True while a date field is half-filled or its year is still being typed. Page
 * handlers return early on it, so the field is left alone until the date is complete
 * (the document-level guard above already does this; this keeps each page safe even
 * where that guard is off).
 */
export function isUnfinishedDateInput(el: HTMLInputElement | null | undefined): boolean {
    if (!el) return false;
    return (el.value === '' && !!el.validity?.badInput) || isUnfinishedDateValue(el.value);
}
